import { useCallback } from 'react'
import { usePostHogContext } from './PostHogProvider'

/**
 * Hook for tracking custom events with PostHog
 * 
 * Usage:
 * const track = usePostHog()
 * track('button_clicked', { button_name: 'Submit', page: 'Dashboard' })
 */
export const usePostHog = () => {
  const { posthog, isEnabled } = usePostHogContext()

  const track = useCallback(
    (eventName: string, properties?: Record<string, any>) => {
      if (isEnabled && posthog) {
        posthog.capture(eventName, properties)
      }
    },
    [posthog, isEnabled]
  )

  const identify = useCallback(
    (userId: string, properties?: Record<string, any>) => {
      if (isEnabled && posthog) {
        posthog.identify(userId, properties)
      }
    },
    [posthog, isEnabled]
  )

  const reset = useCallback(() => {
    if (isEnabled && posthog) {
      posthog.reset()
    }
  }, [posthog, isEnabled])

  const setUserProperties = useCallback(
    (properties: Record<string, any>) => {
      if (isEnabled && posthog) {
        posthog.setPersonProperties(properties)
      }
    },
    [posthog, isEnabled]
  )

  return {
    track,
    identify,
    reset,
    setUserProperties,
    isEnabled,
  }
}
